/**
 *  @class
 *  @function Cart
 */

class Cart {
  constructor() {
    this.container = document.getElementById("Cart");
    this.boundHandleDiscountCode = this.handleDiscountCode.bind(this);
    this.boundHandleRemoveDiscount = this.handleRemoveDiscount.bind(this);
    this.boundHandleClickableDiscount = this.handleClickableDiscount.bind(this);
    this.discountMessageToShow = null;
    this.setupEventListeners();
  }
  onChange(event) {
    if (event.target.matches("input.qty[data-index]")) {
      this.updateQuantity(event.target.dataset.index, event.target.value, this.itemKey(event.target));
    } else if (event.target.getAttribute("id") == "CartSpecialInstructions") {
      this.saveNotes();
    }
  }
  itemKey(element) {
    const row = element.closest("[data-item-key], [data-product-id]");
    return row?.dataset.itemKey || row?.dataset.productId;
  }
  saveNotes() {
    fetch(`${theme.routes.cart_update_url}.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
      body: JSON.stringify({
        note: document.getElementById("CartSpecialInstructions").value,
      }),
    });
  }
  setupEventListeners() {
    // Inner-DOM bindings re-run on every instantiation (innerHTML may have changed).
    this.setupDiscountCodeEvent();
    this.setupDiscountTagsEvent();
    this.setupClickableDiscountEvent();

    // Global / persistent-DOM listeners attach ONCE per page session.
    // Cart is re-instantiated after every cart mutation; without this guard
    // the listeners stack up and one qty edit fires N change.js.
    if (this.container && !this.container.__cartBound) {
      this.container.__cartBound = true;

      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);

      document.addEventListener("cart:refresh", (event) => {
        this.refresh();
      });

      document.addEventListener("line-item:change:end", () => {
        this.setupDiscountCodeEvent();
        this.setupDiscountTagsEvent();
        this.setupClickableDiscountEvent();
      });

      this.container.addEventListener(
        "change",
        this.debouncedOnChange.bind(this)
      );

      // Cart sections can be replaced by another cart component. Keep removal
      // on the stable container so newly rendered links never navigate away.
      this.container.addEventListener("click", (event) => {
        const remove = event.target.closest("a.remove");
        if (!remove || !this.container.contains(remove)) return;
        event.preventDefault();
        if (this.container.classList.contains("cart-disabled")) return;
        const line = remove.dataset.index;
        if (line) this.updateQuantity(line, "0", this.itemKey(remove));
      });
    }
  }
  /** Start 处理折扣码 **/

  _showDiscountMessage() {
    if (!this.discountMessageToShow) return;

    const messageContainer = document.getElementById("cart-discount-message");
    if (!messageContainer) return;

    const messageText = messageContainer.querySelector(".message-text");
    const closeButton = messageContainer.querySelector(".close-message");

    if (messageText) {
      messageText.textContent = this.discountMessageToShow.text;
    }

    messageContainer.className = "discount-message"; // Reset classes
    messageContainer.classList.add(this.discountMessageToShow.type);
    messageContainer.style.display = "flex";

    const closeHandler = () => {
      messageContainer.style.display = "none";
      closeButton.removeEventListener("click", closeHandler);
    };
    if (closeButton) {
      closeButton.addEventListener("click", closeHandler, { once: true });
    }

    this.discountMessageToShow = null;
  }

  _hideDiscountMessage() {
    const messageContainer = document.getElementById("cart-discount-message");
    if (messageContainer) {
      messageContainer.style.display = "none";
    }
  }

  showLoading(button) {
    button.disabled = true;
    const originalText = button.querySelector("span");
    if (originalText) {
      originalText.style.display = "none";
    }
    const spinner = document.createElement("div");
    spinner.className = "btn-loading-spinner";
    button.appendChild(spinner);
  }

  hideLoading(button) {
    const originalText = button.querySelector("span");
    if (originalText) {
      originalText.style.display = "";
    }
    const spinner = button.querySelector(".btn-loading-spinner");
    if (spinner) {
      spinner.remove();
    }
    button.disabled = false;
  }

  setupDiscountCodeEvent() {
    const applyButton = document.getElementById("apply_discount");

    if (applyButton) {
      applyButton.removeEventListener("click", this.boundHandleDiscountCode);
      applyButton.addEventListener("click", this.boundHandleDiscountCode);
    }
  }

  setupClickableDiscountEvent() {
    const clickableCodeLink = document.getElementById(
      "clickable-discount-code"
    );
    if (!clickableCodeLink) return;

    // Check if the code is already applied
    const appliedDiscounts = Array.from(
      document.querySelectorAll(".cart__discout-list span")
    ).map((span) => span.textContent.trim());
    const codeToApply = clickableCodeLink.dataset.code;

    if (
      appliedDiscounts.some(
        (appliedCode) => appliedCode.toLowerCase() === codeToApply.toLowerCase()
      )
    ) {
      clickableCodeLink.style.pointerEvents = "none";
      clickableCodeLink.style.opacity = "0.6";
      clickableCodeLink.style.textDecoration = "none";
    } else {
      clickableCodeLink.removeEventListener(
        "click",
        this.boundHandleClickableDiscount
      );
      clickableCodeLink.addEventListener(
        "click",
        this.boundHandleClickableDiscount
      );
    }
  }

  handleClickableDiscount(event) {
    event.preventDefault();
    const discountCode = event.currentTarget.dataset.code;
    const discountInput = document.getElementById("discount_input");
    const applyButton = document.getElementById("apply_discount");

    if (discountInput && applyButton) {
      discountInput.value = discountCode;
      applyButton.click();
    }
  }

  async handleDiscountCode() {
    const applyButton = document.getElementById("apply_discount");
    const discountInput = document.getElementById("discount_input");
    const discountCode = discountInput.value.trim();

    if (!discountCode) {
      this.discountMessageToShow = {
        text: "Please enter a discount code.",
        type: "error",
      };
      this._showDiscountMessage();
      return;
    }

    this._hideDiscountMessage();
    this.showLoading(applyButton);

    const initialCart = await (await fetch("/cart.js")).json();
    const initialDiscountCode =
      initialCart.cart_level_discount_applications.length > 0
        ? initialCart.cart_level_discount_applications[0].title
        : null;

    try {
      // Attempt to apply the new discount code. This action modifies the cart on the server.
      await fetch(`/discount/${discountCode}`, { redirect: "manual" });

      // Get the new state of the cart after the attempt
      const newCart = await (await fetch("/cart.js")).json();

      // 4. 【核心变更】: 使用 discount_codes 数组进行校验
      const newDiscountApplied = newCart.discount_codes.find(
        (d) =>
          d.code.toLowerCase() === discountCode.toLowerCase() &&
          d.applicable === true
      );

      if (newDiscountApplied) {
        // SUCCESS: The new code was applied.
        // 为了显示节省的金额，我们仍然从 cart_level_discount_applications 获取详细信息
        const discountDetails = newCart.cart_level_discount_applications.find(
          (app) => app.title.toLowerCase() === discountCode.toLowerCase()
        );

        let successText = `Coupon code '${newDiscountApplied.code}' applied!`; // Fallback success message

        if (discountDetails) {
          const savedAmount = new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: newCart.currency,
          }).format(discountDetails.total_allocated_amount / 100);
          successText = `Coupon code '${discountDetails.title}' applied, you saved ${savedAmount}!`;
        }

        this.discountMessageToShow = {
          text: successText,
          type: "success",
        };
      } else {
        // FAILURE: The new code was not applied.
        this.discountMessageToShow = {
          text: `Could not apply discount code '${discountCode}'.`,
          type: "error",
        };
        // The attempt to apply the new code might have removed the old one. We need to restore it.
        if (initialDiscountCode) {
          await fetch(`/discount/${initialDiscountCode}`, {
            redirect: "manual",
          });
        }
      }

      // Refresh the entire cart display from the server, which now has the correct final state.
      await this.refreshCart();
      this._showDiscountMessage();
    } catch (error) {
      console.error("Error applying discount:", error);
      this.discountMessageToShow = {
        text: "An unexpected error occurred.",
        type: "error",
      };
      this._showDiscountMessage();
    } finally {
      this.hideLoading(applyButton);
    }
  }

  async refreshCart() {
    this.container.classList.add("cart-disabled");

    try {
      const sections = this.getSectionsToRender()
        .map((section) => section.section)
        .join(",");
      const response = await fetch(
        `${window.location.pathname}?sections=${sections}`
      );
      const state = await response.text();
      const parsedState = JSON.parse(state);

      if (parsedState.errors) {
        this.displayErrors(null, parsedState.errors);
        return;
      }

      this.renderContents(parsedState, false, true);

      // 触发购物车更新事件
      dispatchCustomEvent("line-item:change:end", {
        cart: parsedState,
      });

      // 如果存在 CartDrawer 类则初始化
      if (typeof CartDrawer !== "undefined") {
        new CartDrawer();
      }
    } catch (error) {
      console.error("Error refreshing cart:", error);
    } finally {
      this.container.classList.remove("cart-disabled");
    }
  }
  setupDiscountTagsEvent() {
    const discountTags = document.querySelectorAll(".remove-discount");
    const warningMessage = document.getElementById(
      "cart-multi-discount-warning"
    );

    if (discountTags.length > 1) {
      discountTags.forEach((tag) => {
        tag.disabled = true;
        tag.style.opacity = "0.5";
        tag.style.cursor = "not-allowed";
      });
      if (warningMessage) {
        warningMessage.style.display = "block";
      }
    } else {
      if (warningMessage) {
        warningMessage.style.display = "none";
      }
      discountTags.forEach((tag) => {
        tag.disabled = false;
        tag.style.opacity = "1";
        tag.style.cursor = "pointer";
        tag.removeEventListener("click", this.boundHandleRemoveDiscount);
        tag.addEventListener("click", this.boundHandleRemoveDiscount);
      });
    }
  }
  async handleRemoveDiscount(event) {
    const button = event.currentTarget;
    const codeToRemove = button.dataset.discountCode;

    this._hideDiscountMessage();
    this.showLoading(button);

    try {
      // Shopify clears the applied discount by visiting /checkout with an empty discount parameter.
      // We use redirect: 'manual' to handle the server's redirect response gracefully.
      await fetch("/checkout?discount=", {
        redirect: "manual",
      });

      // Clear the input field after removal
      const discountInput = document.getElementById("discount_input");
      if (discountInput) {
        discountInput.value = "";
      }

      // After requesting the discount removal, we refresh the cart sections to show the update.
      await this.refreshCart();
      this.discountMessageToShow = {
        text: "Discount code removed.",
        type: "success",
      };
      this._showDiscountMessage();
    } catch (error) {
      console.error("Error removing discount:", error);
      alert("移除折扣码失败");
    } finally {
      this.hideLoading(button);
    }
  }
  /** END 处理折扣码 **/

  getSectionsToRender() {
    return [
      {
        id: "Cart",
        section: "main-cart",
        selector: ".cart-form",
      },
      {
        id: "cart-drawer-toggle",
        section: "cart-bubble",
        selector: ".thb-item-count",
      },
      {
        id: "Cart-Drawer",
        section: "cart-drawer",
        selector: ".side-panel-inner",
      },
    ];
  }
  displayErrors(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.removeAttribute("hidden");
      lineItemError.querySelector(".cart-item__error-text").textContent = message;
      this.container.querySelector(`#CartItem-${line}`)?.classList.remove("loading");
    }
  }
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
  updateQuantity(line, quantity, key) {
    // Line numbers move when gifts or delivery protection are inserted/removed.
    // Never fall back to a stale numeric position when the key is unavailable.
    if (!key) return this.refresh();
    this.container.classList.add("cart-disabled");
    this.container.querySelector(`#CartItem-${line}`)?.classList.add("loading");

    const body = JSON.stringify({
      id: key,
      quantity: Number(quantity),
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    dispatchCustomEvent("line-item:change:start", {
      quantity: quantity,
    });
    let updatedCart;
    return fetch(`${theme.routes.cart_change_url}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
      ...{
        body,
      },
    })
      .then(async (response) => {
        const state = await response.json();
        if (!response.ok || state.errors) {
          throw new Error(state.description || state.errors || "Unable to update this item. Please try again.");
        }
        return state;
      })
      .then((parsedState) => {
        updatedCart = parsedState;
        this.renderContents(parsedState, line, false);
        new CartDrawer();
      })
      .catch((error) => {
        this.displayErrors(line, error.message);
        // A discount can change a line key. Refresh and let the customer retry;
        // retrying with the old line number could change another product.
        this.refresh();
      })
      .finally(() => {
        this.container.classList.remove("cart-disabled");
        this.container.querySelector(`#CartItem-${line}`)?.classList.remove("loading");
        dispatchCustomEvent("line-item:change:end", {
          quantity: quantity,
          cart: updatedCart,
        });
      });
  }
  refresh() {
    this.container.classList.add("cart-disabled");

    let sections = "main-cart";

    fetch(`${window.location.pathname}/?sections=${sections}`)
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          this.displayErrors(line, parsedState.errors);
          this.container.classList.remove("cart-disabled");
          return;
        }

        this.renderContents(parsedState, false, true);

        this.container.classList.remove("cart-disabled");
      });
  }
  renderContents(parsedState, line, refresh) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) ||
        document.getElementById(section.id);

      if (refresh) {
        if (parsedState[section.section]) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState[section.section],
            section.selector
          );
        }
      } else {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }

      if (line && this.container.querySelector(`#CartItem-${line}`)) {
        this.container
          .querySelector(`#CartItem-${line}`)
          .classList.remove("loading");
      }
    });
  }
}
function initializeCart() {
  if (typeof Cart !== "undefined") {
    new Cart();
  }
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeCart, { once: true });
} else {
  initializeCart();
}
function printCart() {
    const firstSegment = window.location.pathname.substring(1).split('/')[0];
    const path = (firstSegment === 'en-gb') ? '/en-gb/pages/cart-print' : '/pages/cart-print';
    window.open(path, '_blank');
}
